import type { Meeting, Sprint, Task } from '@nexo/types';
import { Button, IconButton, SegmentedControl, cn } from '@nexo/ui';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw, Video } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { TeamHeader } from '../../layouts/TeamHeader';
import { useTeam } from '../../layouts/TeamContext';
import { formatTime } from '../../lib/format';
import i18n from '../../lib/i18n';
import { useMeetings, useSprints, useTasks } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { CalendarSyncDialog } from './CalendarSyncDialog';

const MAX_PER_DAY = 3;
const pad = (n: number) => String(n).padStart(2, '0');
const keyOf = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** The 6 weeks (Monday first) shown for a month. */
function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(year, month, 1 - offset + i));
}

type Item = { kind: 'meeting'; at: string; meeting: Meeting } | { kind: 'task'; task: Task };

/** Month view of the team's due dates, meetings and sprints. */
export function CalendarPage() {
  const { t } = useTranslation();
  const { team, openTask } = useTeam();
  const me = useMe();
  const navigate = useNavigate();
  const tasks = useTasks(team.id);
  const meetings = useMeetings(team.id);
  const sprints = useSprints(team.id);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [syncing, setSyncing] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const days = monthGrid(cursor.year, cursor.month);
  const todayKey = keyOf(new Date());
  const shift = (delta: number) =>
    setCursor(({ year, month }) => {
      const date = new Date(year, month + delta, 1);
      return { year: date.getFullYear(), month: date.getMonth() };
    });

  const byDay = new Map<string, Item[]>();
  const add = (key: string, item: Item) => byDay.set(key, [...(byDay.get(key) ?? []), item]);
  for (const meeting of meetings.data ?? []) {
    if (meeting.cancelledAt) continue;
    const mine = meeting.attendees.some((a) => a.id === me.id && a.response !== 'no');
    if (scope === 'mine' && !mine) continue;
    add(keyOf(new Date(meeting.startsAt)), { kind: 'meeting', at: meeting.startsAt, meeting });
  }
  for (const task of tasks.data ?? []) {
    if (!task.dueDate) continue;
    if (scope === 'mine' && !task.assignees.some((a) => a.id === me.id)) continue;
    add(task.dueDate, { kind: 'task', task });
  }
  for (const list of byDay.values()) {
    list.sort((a, b) =>
      a.kind === 'meeting' && b.kind === 'meeting'
        ? a.at.localeCompare(b.at)
        : a.kind === 'meeting'
          ? -1
          : b.kind === 'meeting'
            ? 1
            : 0,
    );
  }
  const sprintOn = (key: string): Sprint | undefined =>
    (sprints.data ?? []).find(
      (s) =>
        s.status !== 'completed' &&
        s.startDate &&
        s.endDate &&
        s.startDate <= key &&
        key <= s.endDate,
    );

  const monthLabel = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(cursor.year, cursor.month, 1));
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(new Date(2024, 0, 1 + i)),
  );

  return (
    <>
      <TeamHeader>
        <CalendarDays className="size-[18px] shrink-0 text-muted" />
        <h1 className="text-[17px] font-semibold tracking-tight">{t('Calendario')}</h1>
        <span className="flex-1" />
        <SegmentedControl
          label={t('Mostrar')}
          value={scope}
          onChange={setScope}
          options={[
            { value: 'mine', label: t('Lo mío') },
            { value: 'all', label: t('Todo el equipo') },
          ]}
        />
        <Button
          variant="secondary"
          size="sm"
          icon={<RefreshCw className="size-4" />}
          onClick={() => setSyncing(true)}
        >
          <span className="max-sm:hidden">{t('Sincronizar')}</span>
        </Button>
      </TeamHeader>

      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 sm:px-6">
        <div className="flex items-center gap-2 py-3">
          <h2 className="text-lg font-semibold first-letter:uppercase">{monthLabel}</h2>
          <span className="flex-1" />
          <IconButton label={t('Mes anterior')} onClick={() => shift(-1)}>
            <ChevronLeft />
          </IconButton>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const now = new Date();
              setCursor({ year: now.getFullYear(), month: now.getMonth() });
            }}
          >
            {t('Hoy')}
          </Button>
          <IconButton label={t('Mes siguiente')} onClick={() => shift(1)}>
            <ChevronRight />
          </IconButton>
        </div>

        <div className="grid grid-cols-7 border-b border-border pb-1 text-center text-[11px] font-semibold tracking-wider text-subtle uppercase">
          {weekdays.map((name) => (
            <span key={name}>{name}</span>
          ))}
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6 overflow-y-auto">
          {days.map((date) => {
            const key = keyOf(date);
            const items = byDay.get(key) ?? [];
            const sprint = sprintOn(key);
            const outside = date.getMonth() !== cursor.month;
            return (
              <div
                key={key}
                className={cn(
                  'flex min-h-24 min-w-0 flex-col gap-0.5 border-r border-b border-border p-1',
                  outside && 'bg-surface-2/40 text-subtle',
                )}
              >
                <span
                  className={cn(
                    'self-end rounded-full px-1.5 text-xs tabular-nums',
                    key === todayKey && 'bg-accent font-semibold text-accent-fg',
                  )}
                >
                  {date.getDate()}
                </span>
                {sprint && (
                  <span
                    title={sprint.name}
                    className="truncate rounded bg-warning-soft px-1 text-[10.5px] font-medium text-warning"
                  >
                    {sprint.name}
                  </span>
                )}
                {items.slice(0, MAX_PER_DAY).map((item) =>
                  item.kind === 'meeting' ? (
                    <button
                      key={item.meeting.id}
                      type="button"
                      onClick={() =>
                        void navigate(`/app/t/${team.id}/meetings?meeting=${item.meeting.id}`)
                      }
                      className="flex items-center gap-1 truncate rounded bg-accent-soft px-1 text-left text-[11px] text-accent hover:opacity-80"
                    >
                      <Video className="size-3 shrink-0" />
                      <span className="tabular-nums">{formatTime(item.at)}</span>
                      <span className="truncate">{item.meeting.title}</span>
                    </button>
                  ) : (
                    <button
                      key={item.task.id}
                      type="button"
                      onClick={() => openTask({ mode: 'edit', taskId: item.task.id })}
                      className={cn(
                        'flex items-center gap-1 truncate rounded px-1 text-left text-[11px] hover:bg-surface-2',
                        item.task.status === 'done' && 'text-subtle line-through',
                      )}
                    >
                      <span
                        className={cn(
                          'size-1.5 shrink-0 rounded-full',
                          item.task.status === 'done'
                            ? 'bg-success'
                            : key < todayKey
                              ? 'bg-danger'
                              : 'bg-text',
                        )}
                      />
                      <span className="truncate">{item.task.title}</span>
                    </button>
                  ),
                )}
                {items.length > MAX_PER_DAY && (
                  <span className="px-1 text-[11px] text-muted">
                    {t('+{{count}} más', { count: items.length - MAX_PER_DAY })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {syncing && <CalendarSyncDialog onClose={() => setSyncing(false)} />}
    </>
  );
}
